import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { faculties as defaultFaculties } from '../../publicSite/facultyData';
import { normalizeFacultiesContent } from '../../publicSite/facultyContent';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { economyProgramOffers, millionairesConnectLessons } from '../../data/economyPrograms';
import { grantSeasonSixCodeAccess, hasSeasonSixAccess, validateSeasonSixCode } from '../../utils/seasonSixAccess';
import { isMillionairesConnectCourse } from '../../utils/resourceAccess';

export default function StudentFacultyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { courses = [] } = useData();
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';
  const [faculties, setFaculties] = useState(() => normalizeFacultiesContent(defaultFaculties));
  const [seasonSixCode, setSeasonSixCode] = useState('');
  const [seasonSixError, setSeasonSixError] = useState('');
  const [seasonSixGranted, setSeasonSixGranted] = useState(() => hasSeasonSixAccess(user));
  const selectedCourseId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('courseId') || '';
  }, []);

  const faculty = useMemo(
    () => faculties.find((item) => item.slug === slug),
    [faculties, slug]
  );
  const programsForFaculty = economyProgramOffers.filter((program) => {
    if (slug === 'millionaires-connect') return program.slug === 'millionaires-connect';
    if (slug === 'building-a-family-economy') return program.slug === 'building-a-family-economy';
    return false;
  });
  const paidVideosUnlocked = Boolean(user?.lmsActive || user?.accessStatus === 'paid');
  const seasonSixUnlocked = Boolean(paidVideosUnlocked || seasonSixGranted);
  const millionairesConnectEnrolled = isMillionairesConnectCourse(courses, user?.courseId, user?.intake);
  const millionairesConnectAccess = millionairesConnectEnrolled && seasonSixUnlocked;
  const hideMillionairesConnectOffers = slug === 'millionaires-connect' && millionairesConnectAccess;

  useEffect(() => {
    if (!useFirebase) return undefined;
    const unsub = onSnapshot(
      doc(db, 'settings', 'publicSite'),
      (snap) => {
        if (!snap.exists()) {
          setFaculties(normalizeFacultiesContent(defaultFaculties));
          return;
        }
        const data = snap.data();
        setFaculties(normalizeFacultiesContent(data?.facultiesContent || defaultFaculties));
      },
      () => {}
    );
    return () => unsub();
  }, [useFirebase]);

  if (!faculty) {
    return (
      <div className="page-content" style={{ maxWidth: '980px', margin: '0 auto' }}>
        <div className="card">
          <div className="card-body">
            <h2 style={{ marginBottom: '8px' }}>Faculty not found</h2>
            <p style={{ color: 'var(--gray-500)' }}>This faculty page is not available.</p>
            <div style={{ marginTop: '12px' }}>
              <button className="btn btn-outline" onClick={() => navigate('/student/registration')}>
                Back to Registration
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleSeasonSixUnlock = () => {
    if (!millionairesConnectEnrolled) {
      setSeasonSixError('Enroll in Millionaires Connect before using an access code.');
      return;
    }
    if (!validateSeasonSixCode(seasonSixCode)) {
      setSeasonSixError('Invalid access code. Please try again.');
      return;
    }
    grantSeasonSixCodeAccess();
    setSeasonSixGranted(true);
    setSeasonSixError('');
    setSeasonSixCode('');
  };

  return (
    <div className="page-content" style={{ maxWidth: '1080px', margin: '0 auto' }}>
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 240px) 1fr', gap: '20px', alignItems: 'center' }}>
          <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-100)', borderRadius: '14px', minHeight: '140px', display: 'grid', placeItems: 'center', padding: '12px' }}>
            <img src={faculty.logo} alt={faculty.title} style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain' }} />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-500)' }}>Student Faculty View</div>
            <h1 style={{ fontSize: '1.8rem', margin: '6px 0 10px' }}>{faculty.title}</h1>
            <p style={{ color: 'var(--gray-600)' }}>{faculty.summary}</p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '14px' }}>
              <button
                className="btn btn-gold"
                onClick={() => navigate(`/student/registration${selectedCourseId ? `?courseId=${encodeURIComponent(selectedCourseId)}` : ''}`)}
              >
                Choose This Faculty
              </button>
              <Link to={`/student/registration${selectedCourseId ? `?courseId=${encodeURIComponent(selectedCourseId)}` : ''}`} className="btn btn-outline">
                Back to Registration
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
        <div className="card">
          <div className="card-header">
            <h3 style={{ fontSize: '1.05rem' }}>Overview</h3>
          </div>
          <div className="card-body" style={{ color: 'var(--gray-600)' }}>
            {faculty.details?.overview || faculty.summary}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 style={{ fontSize: '1.05rem' }}>Program Highlights</h3>
          </div>
          <div className="card-body">
            <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--gray-600)', display: 'grid', gap: '6px' }}>
              {(faculty.details?.highlights || []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 style={{ fontSize: '1.05rem' }}>Outcome</h3>
          </div>
          <div className="card-body" style={{ color: 'var(--gray-600)' }}>
            {faculty.details?.outcome || 'Clear progress, practical implementation, and measurable results.'}
          </div>
        </div>
      </div>

      {programsForFaculty.length > 0 && (
        <div style={{ marginTop: '24px', display: 'grid', gap: '24px' }}>
          <div className="page-header" style={{ marginBottom: 0 }}>
            <h2 className="page-title">Courses on Offer</h2>
            <p className="page-subtitle">Choose the programme that fits your economic journey.</p>
          </div>
          {programsForFaculty.map((program) => (
            <div className="card" key={program.slug}>
              <div className="card-body" style={{ display: 'grid', gap: '20px' }}>
                <img src={program.image} alt={`${program.title} flyer`} style={{ width: '100%', maxHeight: '360px', objectFit: 'contain', borderRadius: '14px' }} />
                <h2>{program.title}</h2>
                {!hideMillionairesConnectOffers && (
                  <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
                    {program.options.map((option) => (
                      <div className="card" key={option.title}>
                        <div className="card-body">
                          <h3>{option.title}</h3>
                          <ul style={{ paddingLeft: '18px', color: 'var(--gray-600)', margin: '12px 0' }}>
                            {option.details.map((detail) => <li key={detail}>{detail}</li>)}
                          </ul>
                          <strong>{option.price}</strong>
                          <div style={{ marginTop: '14px' }}>
                            <button className="btn btn-gold" onClick={() => navigate(`/student/registration${selectedCourseId ? `?courseId=${encodeURIComponent(selectedCourseId)}` : ''}`)}>Enroll Now</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <h3>{program.title} Videos</h3>
                  {!((program.slug === 'millionaires-connect' ? millionairesConnectAccess : false) || (program.slug !== 'millionaires-connect' && paidVideosUnlocked)) ? (
                    <div style={{ display: 'grid', gap: '12px', marginTop: '12px' }}>
                      <div className="badge badge-warning" style={{ textAlign: 'left' }}>
                        {program.slug === 'millionaires-connect'
                          ? 'Season 6 videos are locked. Complete payment or enter the access code to unlock them.'
                          : 'Videos are locked. You need to pay in order for them to be open and playable.'}
                      </div>
                      {program.slug === 'millionaires-connect' && (
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <input
                            className="form-input"
                            placeholder="Enter access code"
                            value={seasonSixCode}
                            onChange={(event) => setSeasonSixCode(event.target.value)}
                            style={{ maxWidth: '260px' }}
                          />
                          <button className="btn btn-outline btn-sm" onClick={handleSeasonSixUnlock}>
                            Unlock with Code
                          </button>
                          {seasonSixError && <div className="badge badge-danger">{seasonSixError}</div>}
                        </div>
                      )}
                      <div>
                        <button
                          className="btn btn-gold btn-sm"
                          onClick={() => navigate(`/student/registration/checkout${selectedCourseId ? `?courseId=${encodeURIComponent(selectedCourseId)}` : ''}`)}
                        >
                          Complete Payment
                        </button>
                      </div>
                    </div>
                  ) : program.slug === 'millionaires-connect' ? (
                    <div className="mc-classroom-entry">
                      <div>
                        <span className="eyebrow">Enrolled programme</span>
                        <h4>{millionairesConnectLessons.length} guided video lessons</h4>
                        <p>Your videos are ready in the classroom. Lessons unlock in order as you complete each video.</p>
                      </div>
                      <Link className="btn btn-primary" to="/student/classroom">Continue in Classroom</Link>
                    </div>
                  ) : (
                    <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginTop: '16px' }}>
                      {program.videos.map(([title, videoId]) => (
                        <div className="card" key={`${title}-${videoId}`} style={{ overflow: 'hidden' }}>
                          <div style={{ aspectRatio: '16 / 9' }}>
                            <iframe title={title} src={`https://www.youtube-nocookie.com/embed/${videoId}`} style={{ width: '100%', height: '100%', border: 0 }} allowFullScreen />
                          </div>
                          <div className="card-body"><strong>{title}</strong></div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
